import type Dexie from "dexie";

export interface OwnedDbConfig {
  /** localStorage key used to remember which user the DB belongs to. */
  ownerKey: string;
  /** Returns the currently active user id, or null if logged out. */
  getCurrentOwnerId: () => string | null;
}

export interface OwnedDbHelpers {
  /** Lazily verifies the DB owner matches the current user; clears DB on mismatch. */
  ensureOwnership: () => Promise<void>;
  /** Forces the next ensureOwnership() call to re-check (use after logout/login). */
  invalidateOwnership: () => void;
  /** Clears every table in the Dexie instance and removes the owner key. */
  clearAll: () => Promise<void>;
}

/**
 * Wraps a Dexie instance with ownership-aware lifecycle helpers so the
 * cached data only ever belongs to a single logged-in user. Lifted from
 * user-app's original DbService so multiple apps can share the pattern.
 */
export function createOwnedDbHelpers(
  db: Dexie,
  config: OwnedDbConfig
): OwnedDbHelpers {
  let ownershipReady: Promise<void> | null = null;

  const clearAll = async (): Promise<void> => {
    const tables = db.tables;
    if (tables.length > 0) {
      await db.transaction("rw", tables, async () => {
        await Promise.all(tables.map((t) => t.clear()));
      });
    }
    localStorage.removeItem(config.ownerKey);
  };

  const ensureOwnership = (): Promise<void> => {
    if (ownershipReady) {
      return ownershipReady;
    }
    ownershipReady = (async () => {
      const activeId = config.getCurrentOwnerId();
      if (!activeId) {
        await clearAll();
        return;
      }
      const stored = localStorage.getItem(config.ownerKey);
      if (stored === activeId) {
        return;
      }
      await clearAll();
      localStorage.setItem(config.ownerKey, activeId);
    })();
    return ownershipReady;
  };

  const invalidateOwnership = (): void => {
    ownershipReady = null;
  };

  return { ensureOwnership, invalidateOwnership, clearAll };
}
